package nl.volleyplanner.web.controller.userprofile

import kotlinx.coroutines.*
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.html.*
import kotlinx.html.js.onClickFunction
import nl.volleyplanner.api.OrganisationUser
import nl.volleyplanner.api.OrganisationUserRole
import nl.volleyplanner.api.auth.AuthLevel
import nl.volleyplanner.web.ApiDataStateFlow
import nl.volleyplanner.web.base.HTMLComponent
import nl.volleyplanner.web.base.component
import nl.volleyplanner.web.base.component.*
import nl.volleyplanner.web.base.icon
import nl.volleyplanner.web.base.spinner
import nl.volleyplanner.web.launchAsync
import nl.volleyplanner.web.service.OrganisationContext
import nl.volleyplanner.web.service.WebUserAndOrganisationService
import nl.volleyplanner.web.util.BootstrapColor
import org.w3c.dom.HTMLElement

class DeleteUserConfirmationModal(
    confirmDeleteAction: (OrganisationUser) -> Unit
) : ModalComponent(
    "Gebruiker verwijderen"
) {
    override val footerButtons = listOf(
        BootstrapButton("Annuleren", ButtonVariant.Secondary, null, mapOf("data-bs-dismiss" to "modal")),
        BootstrapButton("Verwijderen", ButtonVariant.Danger, {
            it.preventDefault()

            userFlow.value?.let { user ->
                confirmDeleteAction.invoke(user)
            }
        })
    )

    private val userFlow = MutableStateFlow<OrganisationUser?>(null)

    fun showModal(user: OrganisationUser) {
        userFlow.value = user
        show()
    }

    override fun FlowContent.renderBody() {
        val user = userFlow.value
        p {
            +"Weet je zeker dat je gebruiker ${user?.email} (${user?.name}) wil verwijderen?"
        }
    }
}

class ManageUsersUsersListComponent(
    private val organisationContext: OrganisationContext,
    private val organisationUsersApiFlow: ApiDataStateFlow<List<OrganisationUser>?, *>,
    private val userInvitedModal: Deferred<UserInvitedModal>
) : HTMLComponent() {

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        val loggedInUser = WebUserAndOrganisationService.userProfileState.value
        val organisationUsers = organisationUsersApiFlow.value
        val deleteUserConfirmationModal = componentScope.async {
            createStandaloneComponent(DeleteUserConfirmationModal() { user ->
                launchAsync {
                    organisationContext.volleyplannerApiClient.organisation.admin.removeUser(user)
                }.onComplete {
                    organisationUsersApiFlow.refresh()
                }
            })
        }

        return consumer.div {
            if (organisationUsers == null) {
                spinner()
                return@div
            }
            organisationUsers.forEach { user ->
                div(classes = "card mt-2") {
                    div(classes = "card-body") {
                        div(classes = "d-flex align-items-center") {
                            div(classes = "fs-3 me-2") {
                                icon("bi-person-circle", color = BootstrapColor.Secondary)
                            }
                            div(classes = "me-auto") {
                                div(classes = "fw-semibold") {
                                    span { +user.name }
                                    when (user.organisationUserRole) {
                                        OrganisationUserRole.Owner -> {
                                            badge(
                                                BootstrapColor.Primary,
                                                style = BadgeStyle.RoundedPill,
                                                small = true,
                                                soft = true
                                            ) { +"Beheerder" }
                                        }
                                        OrganisationUserRole.Invited -> {
                                            badge(
                                                BootstrapColor.Warning,
                                                style = BadgeStyle.RoundedPill,
                                                small = true,
                                                soft = true
                                            ) { +"Uitgenodigd" }
                                        }
                                        else -> {}
                                    }
                                }
                                div(classes = "small text-muted text-secondary") {
                                    icon("bi-envelope", useMargin = true)
                                    +user.email
                                }
                            }
                            div {
                                // Refresh button
                                if (user.organisationUserRole == OrganisationUserRole.Invited) {
                                    button(classes = "btn btn-sm btn-outline-primary") {
                                        onClickFunction = { event ->
                                            event.preventDefault()

                                            componentScope.launch {
                                                val response = organisationContext.volleyplannerApiClient.organisation.admin.refreshUserInvite(user)
                                                userInvitedModal.await()
                                                    .showModal(response)
                                            }
                                        }

                                        icon("bi-arrow-clockwise")
                                        +"Nieuwe link"
                                    }
                                }

                                // Remove user

                                if (user.email != loggedInUser?.email && user.organisationUserRole != OrganisationUserRole.Owner) {
                                    button(classes = "btn btn-link") {
                                        onClickFunction = { event ->
                                            event.preventDefault()

                                            componentScope.launch {
                                                deleteUserConfirmationModal.await().showModal(user)
                                            }
                                        }

                                        icon("bi-trash", color = BootstrapColor.Danger)
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    override suspend fun getState() = organisationUsersApiFlow.subscribeApiResult()
}

class ManageUsersComponent : HTMLComponent() {
    private val organisationUsersApiFlow = ApiDataStateFlow(
        "OrganisationUsersFlow",
        AuthLevel.AdminUser,
        contextFlow = WebUserAndOrganisationService.selectedOrganisationState,
        coroutineScope = componentScope
    ) { organisationContext ->
        organisationContext?.volleyplannerApiClient?.organisation?.admin?.listUsers()
    }

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        val organisationContext = WebUserAndOrganisationService.selectedOrganisationState.value

        val userInvitedModal = componentScope.async {
            createStandaloneComponent(UserInvitedModal())
        }
        val inviteUserModalDeferred = componentScope.async {
            organisationContext?.let {
                createStandaloneComponent(InviteUserModal(organisationContext, userInvitedModal.await(), onUserInvited = {
                    organisationUsersApiFlow.refresh()
                }))
            }
        }

        return consumer.div (classes = "card shadow-sm mt-2") {
            if (organisationContext == null) {
                spinner()
                return@div
            }
            div (classes = "card-body") {
                div (classes = "d-flex align-items-center mb-1") {
                    div {
                        component(ListeningComponent("OrganisationUsersSizeFlow", listOf(organisationUsersApiFlow.subscribeApiResult())) {
                            val organisationUsers = organisationUsersApiFlow.value
                            h5(classes = "card-title mb-0") {
                                if (organisationUsers == null) {
                                    classes += "placeholder-glow"
                                    span (classes = "placeholder col-6") {}
                                } else {
                                    +"Gebruikers (${organisationUsers.size})"
                                }
                            }
                        })
                    }
                    div (classes  = "ms-auto") {
                        button (classes = "btn btn-primary") {
                            onClickFunction = { event ->
                                event.preventDefault()

                                CoroutineScope(Dispatchers.Main).launch {
                                    inviteUserModalDeferred.await()
                                        ?.show()
                                }
                            }

                            icon("bi-person-add", useMargin = true)
                            +"Gebruiker uitnodigen"
                        }
                    }
                }
                component(ManageUsersUsersListComponent(
                    organisationContext,
                    organisationUsersApiFlow,
                    userInvitedModal
                ))
            }
        }
    }

    override suspend fun getState() = WebUserAndOrganisationService.selectedOrganisationState
}